'use client';

import Image, { StaticImageData } from 'next/image';
import { useEffect, useState } from 'react';

import ScrollAnimate from './ScrollAnimate';
import ScrollTranslate from './ScrollTranslate';

import MacSystemDark from '@/assets/screenshots/mock-macbook-system-dark.png';
import MacSystemLight from '@/assets/screenshots/mock-macbook-system-light.png';
import MacDevicesDark from '@/assets/screenshots/mock-macbook-devices-dark.png';
import MacDevicesLight from '@/assets/screenshots/mock-macbook-devices-light.png';
import MacDataDark from '@/assets/screenshots/mock-macbook-data-dark.png';
import MacDataLight from '@/assets/screenshots/mock-macbook-data-light.png';
import PhoneSystemDark from '@/assets/screenshots/mock-iphone-system-dark.png';
import PhoneSystemLight from '@/assets/screenshots/mock-iphone-system-light.png';
import PhoneDevicesDark from '@/assets/screenshots/mock-iphone-devices-dark.png';
import PhoneDevicesLight from '@/assets/screenshots/mock-iphone-devices-light.png';
import PhoneDataDark from '@/assets/screenshots/mock-iphone-data-dark.png';
import PhoneDataLight from '@/assets/screenshots/mock-iphone-data-light.png';

type Variant = { dark: StaticImageData; light: StaticImageData };
type Page = { key: string; mac: Variant; phone: Variant };

const PAGES: Page[] = [
  {
    key: 'system',
    mac: { dark: MacSystemDark, light: MacSystemLight },
    phone: { dark: PhoneSystemDark, light: PhoneSystemLight },
  },
  {
    key: 'devices',
    mac: { dark: MacDevicesDark, light: MacDevicesLight },
    phone: { dark: PhoneDevicesDark, light: PhoneDevicesLight },
  },
  {
    key: 'data',
    mac: { dark: MacDataDark, light: MacDataLight },
    phone: { dark: PhoneDataDark, light: PhoneDataLight },
  },
];

const CYCLE_MS = 5000;

function CyclingImage({
  variants,
  active,
  alt,
  sizes,
  priority,
}: {
  variants: Variant[];
  active: number;
  alt: string;
  sizes?: string;
  priority?: boolean;
}) {
  return (
    <div className="relative">
      {variants.map((variant, i) => (
        <div
          key={i}
          className={`transition-opacity duration-700 ease-in-out ${
            i === 0 ? '' : 'absolute inset-0'
          } ${i === active ? 'opacity-100' : 'opacity-0'}`}
          aria-hidden={i !== active}
        >
          <Image
            src={variant.dark}
            sizes={sizes}
            alt={i === active ? alt : ''}
            className="hidden h-auto w-full object-contain drop-shadow-2xl dark:block"
            priority={priority && i === 0}
          />
          {/* light mode sits on a white section, so the silhouette needs a
              heavier, softer shadow to read as grounded */}
          <Image
            src={variant.light}
            sizes={sizes}
            alt=""
            className="h-auto w-full object-contain [filter:drop-shadow(0_10px_10px_rgba(18,38,34,0.22))_drop-shadow(0_30px_45px_rgba(18,38,34,0.25))] dark:hidden"
            priority={priority && i === 0}
          />
        </div>
      ))}
    </div>
  );
}

/* Angled device mockups cycling through the platform's System, Devices, and
   Data views. Images are theme-aware: the app screenshots match the site's
   light/dark mode. */
export default function DeviceShowcase() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(
      () => setActive((i) => (i + 1) % PAGES.length),
      CYCLE_MS,
    );
    return () => clearInterval(id);
  }, []);

  return (
    <ScrollAnimate delay={300} className="w-full max-w-xl md:max-w-2xl">
      <div className="relative w-full">
        <CyclingImage
          variants={PAGES.map((p) => p.mac)}
          active={active}
          alt="Ensemble control platform desktop view"
          sizes="(max-width: 768px) 100vw, 672px"
          priority
        />

        {/* Phone (foreground) — sized as a percentage of the laptop width,
            anchored to it so the two scale together */}
        <ScrollTranslate
          translateX={0}
          translateY={-15}
          className="absolute -bottom-[2%] -right-[2%] z-20 w-[26%]"
        >
          <ScrollAnimate delay={200}>
            {/* no `sizes` here: small slot-relative values made Chrome skip
                loading the srcset entirely in testing */}
            <CyclingImage
              variants={PAGES.map((p) => p.phone)}
              active={active}
              alt="Ensemble control platform mobile view"
              priority
            />
          </ScrollAnimate>
        </ScrollTranslate>
      </div>
    </ScrollAnimate>
  );
}
